import { cn } from "@/lib/utils";

const SIZES = {
  xs: "h-4 w-4 text-[0.5rem]",
  sm: "h-5 w-5 text-[0.55rem]",
  md: "h-6 w-6 text-[0.6rem]",
  lg: "h-8 w-8 text-[0.7rem]",
  xl: "h-12 w-12 text-sm",
} as const;

export type CrestSize = keyof typeof SIZES;

interface ClubCrestProps {
  clubName: string;
  /** Club brand colors from the clubs table. Fallback palette is derived from the name. */
  primary?: string | null;
  secondary?: string | null;
  size?: CrestSize;
  className?: string;
}

function initials(name: string) {
  const cleaned = name
    .replace(/^(FC|AC|SC|AS|SS|1\.|TSG|VfB|VfL|SV|IFK|FK|BK|SK)\s+/i, "")
    .trim();
  const parts = cleaned.split(/[\s-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return parts
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

/** Deterministic hash so every club keeps a stable shape/palette */
function hash(str: string) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Fallback brand colors when a club has no curated pair yet */
function fallbackColors(name: string): [string, string] {
  const h = hash(name);
  const hue = h % 360;
  const primary = `hsl(${hue} 62% 34%)`;
  const secondary = "#FFFFFF";
  return [primary, secondary];
}

function isLight(hex: string) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255,
    g = (n >> 8) & 255,
    b = n & 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 150;
}

type Variant = "circle" | "shield" | "split" | "stripes" | "diagonal";

const VARIANTS: Variant[] = ["circle", "shield", "split", "stripes", "diagonal"];

/**
 * Trademark-safe club identity mark: an SVG monogram built from the club's
 * own brand colors and initials. No official crest artwork is used.
 */
export function ClubCrest({
  clubName,
  primary,
  secondary,
  size = "md",
  className,
}: ClubCrestProps) {
  const [p, s] = primary
    ? [primary, secondary || (isLight(primary) ? "#1A1A1A" : "#FFFFFF")]
    : fallbackColors(clubName);
  const variant = VARIANTS[hash(clubName) % VARIANTS.length];
  const uid = `cc-${hash(clubName + variant)}`;
  const letters = initials(clubName);

  return (
    <span
      className={cn("inline-flex shrink-0 items-center justify-center", SIZES[size], className)}
      aria-hidden
    >
      <svg viewBox="0 0 24 24" className="h-full w-full drop-shadow-sm">
        <defs>
          <clipPath id={uid}>
            {variant === "circle" ? (
              <circle cx="12" cy="12" r="11" />
            ) : (
              <path d="M12 1 L22 4.5 V13 C22 18.5 17.5 22.2 12 23.5 C6.5 22.2 2 18.5 2 13 V4.5 Z" />
            )}
          </clipPath>
        </defs>
        <g clipPath={`url(#${uid})`}>
          <rect width="24" height="24" fill={p} />
          {variant === "split" && <rect x="12" width="12" height="24" fill={s} opacity="0.9" />}
          {variant === "diagonal" && <path d="M0 24 L24 0 V24 Z" fill={s} opacity="0.9" />}
          {variant === "stripes" && (
            <>
              <rect x="4.8" width="4.8" height="24" fill={s} opacity="0.9" />
              <rect x="14.4" width="4.8" height="24" fill={s} opacity="0.9" />
            </>
          )}
        </g>
        {variant === "circle" ? (
          <circle cx="12" cy="12" r="11" fill="none" stroke={s} strokeWidth="1" opacity="0.6" />
        ) : (
          <path
            d="M12 1 L22 4.5 V13 C22 18.5 17.5 22.2 12 23.5 C6.5 22.2 2 18.5 2 13 V4.5 Z"
            fill="none"
            stroke={s}
            strokeWidth="1"
            opacity="0.6"
          />
        )}
        <text
          x="12"
          y="15.5"
          textAnchor="middle"
          fontFamily="Archivo, sans-serif"
          fontWeight="800"
          fontSize="8.5"
          fill={variant === "split" || variant === "stripes" || variant === "diagonal" ? (isLight(p) ? "#1A1A1A" : "#FFFFFF") : s}
        >
          {letters}
        </text>
      </svg>
    </span>
  );
}

export default ClubCrest;
